import type { Market, FundingRateHistory } from "./types";
import {
  MARKETS,
  type MarketSymbol,
  FUNDING_RATE_INTERVAL_HOURS,
  MAX_FUNDING_RATE,
  MIN_FUNDING_RATE,
} from "../constants";
import { debug } from "@/lib/debug";

const MARKETS_KEY = "solana_perp_markets";
const FUNDING_HISTORY_KEY = "solana_perp_funding_history";

// Type for dynamic market configuration from API
type DynamicMarketConfig = {
  symbol: string;
  baseSymbol: string;
  quoteSymbol: string;
  binanceSymbol?: string;
};

// Perpetual Market Metadata Cache (localStorage)
// Stores market prices, funding rates, and history for UI display
export class PerpMarketProgram {
  /**
   * Initialize all markets (legacy - uses hardcoded MARKETS constant)
   * @deprecated Use initializeDynamicMarkets() instead
   */
  static initializeMarkets(): Market[] {
    const existingMarkets = this.getAllMarkets();

    const configuredSymbols = Object.keys(MARKETS);
    const hasMismatch =
      existingMarkets.length !== configuredSymbols.length ||
      configuredSymbols.some((symbol) =>
        !existingMarkets.some((market) => market.symbol === symbol)
      );

    if (existingMarkets.length > 0 && !hasMismatch) {
      return existingMarkets;
    }

    const markets: Market[] = Object.keys(MARKETS).map((symbol) => {
      const config = MARKETS[symbol as MarketSymbol];
      return {
        symbol: symbol as MarketSymbol,
        baseSymbol: config.baseSymbol,
        quoteSymbol: config.quoteSymbol,
        currentPrice: 0,
        oraclePrice: 0,
        fundingRate: this.generateRandomFundingRate(),
        nextFundingTime: this.calculateNextFundingTime(),
        openInterest: 0,
        volume24h: 0,
        longShortRatio: 1.0,
        priceChange24h: 0,
        priceChangePercent24h: 0,
        price24hAgo: 0,
      };
    });

    this.saveAllMarkets(markets);
    return markets;
  }

  /**
   * Initialize markets dynamically from API data
   * This replaces the hardcoded MARKETS constant approach
   */
  static initializeDynamicMarkets(marketConfigs: DynamicMarketConfig[]): Market[] {
    const existingMarkets = this.getAllMarkets();
    
    // Get list of symbols from configs
    const configuredSymbols = marketConfigs.map(m => m.symbol);
    
    // Check if we need to reinitialize (new markets added or removed)
    const hasMismatch =
      existingMarkets.length !== configuredSymbols.length ||
      configuredSymbols.some((symbol) =>
        !existingMarkets.some((market) => market.symbol === symbol)
      );

    if (existingMarkets.length > 0 && !hasMismatch) {
      // Markets already initialized and match config
      return existingMarkets;
    }

    // Initialize new markets or update existing ones
    const markets: Market[] = marketConfigs.map((config) => {
      // Check if market already exists to preserve price data
      const existing = existingMarkets.find(m => m.symbol === config.symbol);
      
      if (existing) {
        // Keep existing market data (prices, stats)
        return existing;
      }
      
      // Create new market
      return {
        symbol: config.symbol as MarketSymbol,
        baseSymbol: config.baseSymbol,
        quoteSymbol: config.quoteSymbol,
        currentPrice: 0,
        oraclePrice: 0,
        fundingRate: this.generateRandomFundingRate(),
        nextFundingTime: this.calculateNextFundingTime(),
        openInterest: 0,
        volume24h: 0,
        longShortRatio: 1.0,
        priceChange24h: 0,
        priceChangePercent24h: 0,
        price24hAgo: 0,
      };
    });

    this.saveAllMarkets(markets);
    debug.log('PerpMarketProgram', `Initialized ${markets.length} dynamic markets`);
    return markets;
  }

  /**
   * Get market by symbol
   * Now supports both hardcoded and dynamic market symbols
   */
  static getMarket(symbol: string): Market | null {
    const markets = this.getAllMarkets();
    return markets.find((m) => m.symbol === symbol) || null;
  }

  /**
   * Update market price
   * Now supports both hardcoded and dynamic market symbols
   */
  static updateMarketPrice(symbol: string, price: number, oraclePrice?: number): Market | null {
    const markets = this.getAllMarkets();
    
    // Don't auto-initialize here - let use-market-price-sync handle initialization
    // This prevents creating markets with wrong config
    if (markets.length === 0) {
      console.debug("Markets not initialized yet, skipping price update for:", symbol);
      return null;
    }
    
    const market = markets.find((m) => m.symbol === symbol);

    if (!market) {
      console.debug("Market not found in initialized markets:", symbol);
      return null;
    }

    // Set initial 24h ago price if not set
    if (!market.price24hAgo || market.price24hAgo === 0) {
      market.price24hAgo = price;
    }

    market.currentPrice = price;
    // Use provided oracle price if available, otherwise preserve existing oracle price
    // Only fall back to market price if no oracle price exists yet
    if (oraclePrice !== undefined) {
      market.oraclePrice = oraclePrice;
    } else if (!market.oraclePrice || market.oraclePrice === 0) {
      // Initialize oracle price to market price if not set (first load)
      market.oraclePrice = price;
    }
    // Otherwise, keep existing oracle price (set by use-oracle-sync from backend)
    
    // Calculate 24h changes
    this.calculate24hChange(market);
    
    this.saveAllMarkets(markets);

    return market;
  }

  /**
   * Update market statistics
   * Now supports both hardcoded and dynamic market symbols
   */
  static updateMarketStats(
    symbol: string,
    stats: Partial<Omit<Market, "symbol" | "baseSymbol" | "quoteSymbol">>
  ): Market | null {
    const markets = this.getAllMarkets();
    
    // Don't auto-initialize here - let use-market-price-sync handle initialization
    if (markets.length === 0) {
      console.debug("Markets not initialized yet, skipping stats update for:", symbol);
      return null;
    }
    
    const market = markets.find((m) => m.symbol === symbol);

    if (!market) {
      console.debug("Market not found in initialized markets:", symbol);
      return null;
    }

    Object.assign(market, stats);
    this.saveAllMarkets(markets);

    return market;
  }

  /**
   * Update oracle price for a market
   */
  static updateOraclePrice(symbol: string, oraclePrice: number): void {
    const markets = this.getAllMarkets();
    const market = markets.find(m => m.symbol === symbol);
    
    if (market) {
      market.oraclePrice = oraclePrice;
      this.saveAllMarkets(markets);
    } else {
      console.debug("Market not found for oracle price update:", symbol);
    }
  }

  /**
   * Calculate and update funding rates
   */
  static updateFundingRates(): void {
    const markets = this.getAllMarkets();
    const now = Date.now();

    markets.forEach((market) => {
      if (now >= market.nextFundingTime) {
        // Record current funding rate in history
        this.addFundingHistory(market.symbol, market.fundingRate);

        // Generate new funding rate
        market.fundingRate = this.generateRandomFundingRate();
        market.nextFundingTime = this.calculateNextFundingTime();
      }
    });

    this.saveAllMarkets(markets);
  }

  /**
   * Get funding rate history for a market
   */
  static getFundingHistory(
    symbol: MarketSymbol,
    limit = 24
  ): FundingRateHistory[] {
    const history = this.getAllFundingHistory();
    return history
      .filter((h) => h.market === symbol)
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, limit);
  }

  /**
   * Calculate next funding time (every 8 hours)
   */
  private static calculateNextFundingTime(): number {
    const now = Date.now();
    const hours = FUNDING_RATE_INTERVAL_HOURS;
    const interval = hours * 60 * 60 * 1000;
    return Math.ceil(now / interval) * interval;
  }

  /**
   * Generate a random funding rate within bounds
   */
  private static generateRandomFundingRate(): number {
    const range = MAX_FUNDING_RATE - MIN_FUNDING_RATE;
    return MIN_FUNDING_RATE + Math.random() * range;
  }

  /**
   * Calculate 24h price change and percentage
   */
  private static calculate24hChange(market: Market): void {
    if (!market.price24hAgo || market.price24hAgo === 0) {
      market.priceChange24h = 0;
      market.priceChangePercent24h = 0;
      return;
    }

    market.priceChange24h = market.currentPrice - market.price24hAgo;
    market.priceChangePercent24h = 
      (market.priceChange24h / market.price24hAgo) * 100;
  }

  /**
   * Update 24h ago price (should be called periodically)
   */
  static update24hPrices(): void {
    const markets = this.getAllMarkets();
    markets.forEach((market) => {
      if (market.currentPrice > 0) {
        market.price24hAgo = market.currentPrice;
      }
    });
    this.saveAllMarkets(markets);
  }

  /**
   * Add funding rate to history
   */
  private static addFundingHistory(
    symbol: MarketSymbol,
    rate: number
  ): void {
    const history = this.getAllFundingHistory();
    history.push({
      timestamp: Date.now(),
      rate,
      market: symbol,
    });

    // Keep only last 100 records per market
    const marketHistory = history.filter((h) => h.market === symbol);
    if (marketHistory.length > 100) {
      const toRemove = marketHistory
        .sort((a, b) => a.timestamp - b.timestamp)
        .slice(0, marketHistory.length - 100);

      const filtered = history.filter(
        (h) => !toRemove.some((r) => r.timestamp === h.timestamp && r.market === h.market)
      );
      this.saveFundingHistory(filtered);
    } else {
      this.saveFundingHistory(history);
    }
  }

  // Storage helpers
  /**
   * Get all markets from localStorage
   * Public so other components can read the initialized markets
   */
  static getAllMarkets(): Market[] {
    const stored = localStorage.getItem(MARKETS_KEY);
    if (!stored) {
      return [];
    }
    return JSON.parse(stored);
  }

  private static saveAllMarkets(markets: Market[]): void {
    localStorage.setItem(MARKETS_KEY, JSON.stringify(markets));
  }

  private static getAllFundingHistory(): FundingRateHistory[] {
    const stored = localStorage.getItem(FUNDING_HISTORY_KEY);
    return stored ? JSON.parse(stored) : [];
  }

  private static saveFundingHistory(history: FundingRateHistory[]): void {
    localStorage.setItem(FUNDING_HISTORY_KEY, JSON.stringify(history));
  }

  /**
   * Clear all data (for testing)
   */
  static clearAll(): void {
    localStorage.removeItem(MARKETS_KEY);
    localStorage.removeItem(FUNDING_HISTORY_KEY);
  }
}

